/** @jsxImportSource @emotion/react */
import { css, keyframes } from "@emotion/react";
import { Rail, RailFooter, RailLogo, RailNav, RailSection, RailTile } from "@atomic/ui-kit";
import {
  Atom,
  Blocks,
  BookOpen,
  ChartColumn,
  GlassWater,
  HardDrive,
  Sparkles,
  TrendingUp,
  Warehouse,
  Waypoints,
} from "lucide-react";
import { type FC, type ReactNode, useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router";
import { isDataUser } from "../../access/roles";
import { useAuth } from "../../hooks/useAuth";
import { theme, alpha } from "../../styles/theme/theme";

type IconComponent = FC<{ size?: number; strokeWidth?: number; "aria-hidden"?: boolean }>;

interface NavItem {
  href: string;
  name: string;
  icon: IconComponent;
  /** The tile toggles the Nucleus dock; it never navigates. */
  isNucleus?: boolean;
  /** A module that isn't available yet: the kit's locked tile — inert, muted,
   *  a lock on its icon. The `href` is still declared so the render path stays
   *  uniform; it's just never navigated to. */
  locked?: boolean;
  /** Renders camouflaged as a `locked` tile, but is secretly a dev escape hatch:
   *  clicking it {@link REQUIRED_CLICKS} times in quick succession tints it
   *  progressively bluer and then bounces + unlocks the module. The unlock is
   *  persisted in localStorage under {@link NavItem.unlockKey}, so a module can
   *  ride onto dev for feedback without being exposed to everyone. Camouflage,
   *  not access control — the route stays registered, so anyone who types the
   *  URL still gets in. */
  secret?: boolean;
  /** localStorage key holding this tile's unlock. Required with `secret`, and
   *  each secret tile MUST have its own — sharing one would make unlocking any
   *  module reveal every other unproven module with it. */
  unlockKey?: string;
}

// Per-module "dev-mode" unlock keys — see NavItem.secret. One key per tile so a
// module graduates locked -> secret (reachable by whoever knows the combo, for
// feedback) -> public independently of every other. Namespace a key by company
// code, per the client-identity contract in CLAUDE.md.
//
// No tile declares one today. The next module that ships to dev early adds its
// own key here beside `secret: true`.
const REQUIRED_CLICKS = 5;
const COMBO_RESET_MS = 1500; // pause longer than this and the click streak resets
const UNLOCK_BOUNCE_MS = 480; // hold the bounce before flipping to the live tile + navigating

const secretBounce = keyframes`
  0%   { transform: scale(1); }
  25%  { transform: scale(0.8); }
  50%  { transform: scale(1.2); }
  70%  { transform: scale(0.92); }
  100% { transform: scale(1); }
`;

/* The secret tile's look on top of the kit's locked tile. At rest (progress 0)
   it restates nothing: it IS the kit's locked tile, whatever the kit draws for
   one, so it hides in plain sight among the genuinely locked modules — the one
   thing it hides is the hand the kit shows a locked tile that still listens,
   since a genuinely locked tile shows a no. Each click raises `progress`
   (0→1), tinting the tile bluer and growing a blue ring; the final click flips
   `bouncing` on for the spring animation right before the module goes live.
   The kit's locked rules carry the attribute selector, so these do too, at the
   same specificity, and win by source order. */
const secretStyles = (progress: number, bouncing: boolean) => css`
  &[data-locked],
  &[data-locked]:hover {
    transition:
      background-color 0.18s ease,
      color 0.18s ease,
      box-shadow 0.18s ease,
      opacity 0.18s ease;
  }
  &[data-locked]:not(:disabled) {
    cursor: not-allowed;
  }
  ${progress > 0
    ? css`
        &[data-locked],
        &[data-locked]:hover,
        &[data-locked]:not(:disabled) {
          cursor: pointer;
          opacity: 1;
          background-color: ${alpha(theme.colors.primary, 0.08 + progress * 0.32)};
          color: ${alpha(theme.colors.primary, 0.55 + progress * 0.45)};
          box-shadow: 0 0 0 ${(1 + progress * 2).toFixed(2)}px ${alpha(theme.colors.primary, 0.15 + progress * 0.35)};
          /* animation-name interpolates the keyframes object directly — folding
             it into an animation shorthand string instead makes Emotion warn
             that the keyframes landed in a plain string. */
          animation-name: ${bouncing ? secretBounce : "none"};
          animation-duration: ${UNLOCK_BOUNCE_MS}ms;
          animation-timing-function: cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      `
    : ""}
`;

// The Replenishment page split three ways (LMN experiment): Supply is its
// Balance Walk alone; Sparkling and Drink Mix are its Production + Transfers
// scoped to one product class each, with independently locked plans.
// /replenishment itself stays routed — links and bookmarks keep working —
// it just has no tile.
const primaryItems: NavItem[] = [
  { href: "/demand", name: "Demand", icon: TrendingUp as IconComponent },
  { href: "/supply", name: "Supply", icon: Warehouse as IconComponent },
  { href: "/sparkling", name: "Sparkling", icon: Sparkles as IconComponent },
  { href: "/drink-mix", name: "Drink Mix", icon: GlassWater as IconComponent },
  { href: "/materials", name: "Materials", icon: Blocks as IconComponent, locked: true },
];

const secondaryItems: NavItem[] = [
  { href: "/data", name: "Data", icon: HardDrive as IconComponent },
  { href: "/reporting", name: "Reporting", icon: ChartColumn as IconComponent },
  { href: "/digital-twin", name: "Digital Twin", icon: Waypoints as IconComponent },
  { href: "/docs", name: "Docs", icon: BookOpen as IconComponent, locked: true },
  { href: "/nucleus", name: "Nucleus", icon: Atom as IconComponent, isNucleus: true, locked: true },
];

interface SidebarProps {
  isNucleusDockOpen?: boolean;
  onNucleusToggle?: () => void;
  /** The rail's floor: the feedback trigger and the account menu. */
  footer?: ReactNode;
}

/**
 * The rail of modules, on the kit's Rail (Signal: the caption, the tile's
 * radius, the selected tile in the accent on its soft tint, the locked
 * treatment are the kit's; this file decides only which tiles exist and what
 * each one is). An Engine group of the planning modules and a Tools group of
 * the surfaces that exist today. A tile is the router's link, so cmd/ctrl- and
 * middle-click open a tab; re-clicking the tab you're already on resets it to
 * the tab's home (the reporting embed freezes its report at mount, so the
 * nonce in the state makes ReportingView remount it; other views ignore it).
 */
export const Sidebar: FC<SidebarProps> = ({ isNucleusDockOpen = false, onNucleusToggle, footer }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const dataUser = isDataUser(user);

  // Dev-unlock state, keyed by NavItem.unlockKey so each secret tile is
  // independent: the persisted unlocks, the in-progress click streak (which tile
  // + how far), and which tile is mid-bounce. Per-tile because with a single
  // shared count, clicking two secret tiles alternately would unlock whichever
  // one happened to take the 5th click.
  const [unlockedKeys, setUnlockedKeys] = useState<ReadonlySet<string>>(() => {
    try {
      return new Set(
        primaryItems
          .map((i) => i.unlockKey)
          .filter((k): k is string => !!k && localStorage.getItem(k) === "1"),
      );
    } catch {
      return new Set();
    }
  });
  const [combo, setCombo] = useState<{ key: string; count: number } | null>(null);
  const [unlockingKey, setUnlockingKey] = useState<string | null>(null);
  const comboResetTimer = useRef<number | null>(null);
  const unlockTimer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (comboResetTimer.current) window.clearTimeout(comboResetTimer.current);
      if (unlockTimer.current) window.clearTimeout(unlockTimer.current);
    },
    [],
  );

  const handleSecretClick = (href: string, key: string) => {
    if (unlockingKey) return; // already mid-bounce; ignore extra mashing
    if (comboResetTimer.current) window.clearTimeout(comboResetTimer.current);

    // A click on a DIFFERENT secret tile restarts the streak rather than
    // inheriting the previous tile's progress.
    const next = combo?.key === key ? combo.count + 1 : 1;
    if (next >= REQUIRED_CLICKS) {
      setCombo({ key, count: REQUIRED_CLICKS });
      setUnlockingKey(key); // play the bounce on the still-mounted secret tile…
      unlockTimer.current = window.setTimeout(() => {
        try {
          localStorage.setItem(key, "1");
        } catch {
          /* private mode / storage disabled — unlock for this session only */
        }
        // …then flip it to the live, navigable tile
        setUnlockedKeys((prev) => new Set(prev).add(key));
        setUnlockingKey(null);
        setCombo(null);
        navigate(href);
      }, UNLOCK_BOUNCE_MS);
      return;
    }

    setCombo({ key, count: next });
    comboResetTimer.current = window.setTimeout(() => setCombo(null), COMBO_RESET_MS);
  };

  const renderItem = (item: NavItem) => {
    if (item.locked) return <RailTile key={item.href} label={item.name} icon={item.icon} locked />;
    // A secret tile only behaves secretly until it's been unlocked; afterwards
    // it's an ordinary tile. No unlockKey ⇒ nothing to persist against, so it
    // can never unlock — treat it as a plain locked tile rather than a trap.
    const unlockKey = item.unlockKey;
    if (item.secret && !unlockKey) return <RailTile key={item.href} label={item.name} icon={item.icon} locked />;
    if (item.secret && unlockKey && !unlockedKeys.has(unlockKey)) {
      const unlocking = unlockingKey === unlockKey;
      const progress = unlocking ? 1 : (combo?.key === unlockKey ? combo.count : 0) / REQUIRED_CLICKS;
      // Looks locked (muted + lock badge) at rest and while mid-streak, but it
      // is NOT inert — it has to receive the unlock clicks. Every click feeds
      // the combo, cmd/ctrl-click included: a tile that is a button, not a
      // link, cannot leak the module into a new tab before it's unlocked.
      return (
        <RailTile
          key={item.href}
          label={item.name}
          icon={item.icon}
          locked
          disabled={false}
          onClick={() => handleSecretClick(item.href, unlockKey)}
          css={secretStyles(progress, unlocking)}
        />
      );
    }
    if (item.isNucleus) {
      return (
        <RailTile
          key={item.href}
          label={item.name}
          icon={item.icon}
          pressed={isNucleusDockOpen}
          active={isNucleusDockOpen}
          onClick={onNucleusToggle}
          title={`${item.name} (or Ctrl+Shift+P to open new prompt)`}
        />
      );
    }
    const pathMatch = pathname === item.href || pathname.startsWith(item.href + "/");
    return (
      <RailTile key={item.href} label={item.name} icon={item.icon} active={pathMatch} asChild>
        <NavLink
          to={item.href}
          onClick={(e) => {
            // Re-clicking the tab you're already on resets it to the tab's home,
            // with a nonce stamped at click time: the reporting embed freezes
            // its report at mount, so ReportingView remounts it on a new nonce
            // (other views ignore it). A modified click stays the browser's —
            // a new tab — and any other click is the router's own navigation.
            if (!pathMatch || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
            e.preventDefault();
            navigate(item.href, { state: { resetEmbed: Date.now() } });
          }}
        />
      </RailTile>
    );
  };

  const logo = (
    <RailLogo>
      <img src="/images/lmnt-logo.png" alt="Lmnt" />
    </RailLogo>
  );

  // A data user gets exactly two tiles: Data and Reporting. The whole Engine
  // section and every other tool is withheld, matching the RoleGuard route
  // allowlist.
  if (dataUser) {
    return (
      <Rail>
        {logo}
        <RailNav>
          <RailSection label="Tools" pinned>
            {secondaryItems.filter((i) => i.href === "/data" || i.href === "/reporting").map(renderItem)}
          </RailSection>
        </RailNav>
        {footer ? <RailFooter>{footer}</RailFooter> : null}
      </Rail>
    );
  }

  return (
    <Rail>
      {logo}
      <RailNav>
        <RailSection label="Engine">{primaryItems.map(renderItem)}</RailSection>
        <RailSection label="Tools" pinned>
          {secondaryItems
            .map((i) =>
              // Nucleus: unlocked for admitted callers, still the locked
              // teaser tile for everyone else. The gateway decides admission;
              // this tile reads the answer (NUC-296). Locked while WhoAmI is
              // still loading, which is the safe direction: a tile that
              // unlocks early would open a dock the guard then refuses.
              i.isNucleus ? { ...i, locked: !user?.nucleusAdmitted } : i,
            )
            .map(renderItem)}
        </RailSection>
      </RailNav>
      {footer ? <RailFooter>{footer}</RailFooter> : null}
    </Rail>
  );
};
